import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Department, ThemeColors } from '../../types';

interface DepartmentNavProps {
  departments: Department[];
  selectedDepartmentId: number | null;
  onDepartmentClick: (departmentId: number | null) => void;
  themeColors?: ThemeColors;
  inline?: boolean;
}

export function DepartmentNav({
  departments,
  selectedDepartmentId,
  onDepartmentClick,
  themeColors,
  inline = false,
}: DepartmentNavProps) {
  const activeColor = themeColors?.left_side_active_color || '#ffffff';
  const itemColor = themeColors?.left_side_color || '#e5e7eb';

  const deptColor = (active: boolean) => (active ? activeColor : itemColor);

  const departmentScrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [hoveredDepartmentId, setHoveredDepartmentId] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const selectedCategoryId = urlParams.get('categoryId') ? Number(urlParams.get('categoryId')) : null;

  const isActiveDepartment = (departmentId: number | null) => {
    if (departmentId === null) {
      return selectedDepartmentId === null;
    }
    return Number(selectedDepartmentId) === Number(departmentId);
  };

  const checkScrollArrows = () => {
    const container = departmentScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
  };

  const scrollDepartments = (direction: 'left' | 'right') => {
    const container = departmentScrollRef.current;
    if (!container) return;

    const scrollAmount = 200;
    const newScrollLeft = direction === 'left'
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    checkScrollArrows();
    const container = departmentScrollRef.current;
    if (!container) return;

    const handleScroll = () => checkScrollArrows();
    const handleResize = () => checkScrollArrows();

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [departments]);

  const handleCategoryClick = (departmentId: number, categoryId: number) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('departmentId', departmentId.toString());
    searchParams.set('categoryId', categoryId.toString());
    searchParams.delete('page');
    navigate(`/?${searchParams.toString()}`);
    setHoveredDepartmentId(null);
  };

  const handleDepartmentMouseEnter = (departmentId: number, event: React.MouseEvent<HTMLButtonElement>) => {
    setHoveredDepartmentId(departmentId);

    const button = event.currentTarget;
    const buttonRect = button.getBoundingClientRect();
    if (containerRef.current) {
      const parentRect = containerRef.current.getBoundingClientRect();
      setDropdownPosition(buttonRect.left - parentRect.left);
    }
  };

  const handleNavMouseLeave = () => {
    setHoveredDepartmentId(null);
  };

  const hoveredDepartment = hoveredDepartmentId
    ? departments.find(d => d.id === hoveredDepartmentId)
    : null;

  const isAllActive = isActiveDepartment(null);

  const navContent = (
    <div ref={containerRef} className={`relative flex items-center min-w-0 ${inline ? 'h-full w-full' : 'h-11'}`} onMouseLeave={handleNavMouseLeave}>
      {/* Left Arrow */}
      {showLeftArrow && (
        <button
          onClick={() => scrollDepartments('left')}
          className="absolute left-0 z-10 p-2 bg-gray-800/90 hover:bg-gray-800 text-white rounded-md shadow-lg transition-all outline-none focus:outline-none focus-visible:outline-none"
          aria-label="Scroll departments left"
        >
          <ChevronLeft className="w-4 h-4" aria-hidden="true" />
        </button>
      )}

      {/* Department List */}
      <div
        ref={departmentScrollRef}
        className="flex items-center overflow-x-auto scrollbar-hide w-full min-w-0 px-6"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', gap: '2rem' }}
      >
        {/* "All Departments" — visually distinct: separator after it */}
        <div className="flex items-center flex-shrink-0" style={{ borderRight: `1px solid color-mix(in srgb, ${itemColor} 20%, transparent)`, paddingRight: '2rem' }}>
          <button
            onClick={() => onDepartmentClick(null)}
            onMouseEnter={e => { if (!isAllActive) e.currentTarget.style.backgroundColor = `color-mix(in srgb, ${itemColor} 14%, transparent)`; }}
            onMouseLeave={e => { if (!isAllActive) e.currentTarget.style.backgroundColor = ''; }}
            className={`flex-shrink-0 text-xs uppercase tracking-wider transition-all duration-150 px-3 py-1.5 rounded-md outline-none focus:outline-none focus-visible:outline-none ${!isAllActive ? 'active:brightness-[0.6]' : ''}`}
            style={{
              color: deptColor(isAllActive),
              backgroundColor: isAllActive ? `color-mix(in srgb, ${activeColor} 35%, transparent)` : undefined,
              fontWeight: isAllActive ? 700 : 500,
              outline: 'none',
              boxShadow: 'none',
            }}
            aria-current={isAllActive ? 'page' : undefined}
          >
            All Departments
          </button>
        </div>

        {departments.map(dept => {
          const active = isActiveDepartment(dept.id);
          return (
            <button
              key={dept.id}
              onClick={() => onDepartmentClick(dept.id)}
              onMouseEnter={e => {
                handleDepartmentMouseEnter(dept.id, e);
                if (!active) e.currentTarget.style.backgroundColor = `color-mix(in srgb, ${itemColor} 14%, transparent)`;
              }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = ''; }}
              className={`flex-shrink-0 flex items-center gap-1 text-xs uppercase tracking-wider transition-all duration-150 whitespace-nowrap px-3 py-1.5 rounded-md outline-none focus:outline-none focus-visible:outline-none ${!active ? 'active:brightness-[0.6]' : ''}`}
              style={{
                color: deptColor(active),
                backgroundColor: active ? `color-mix(in srgb, ${activeColor} 35%, transparent)` : undefined,
                fontWeight: active ? 700 : 600,
                outline: 'none',
                boxShadow: 'none',
              }}
              aria-current={active ? 'page' : undefined}
              aria-expanded={hoveredDepartmentId === dept.id}
            >
              <span>{dept.name} {dept.memo && `(${dept.memo})`}</span>
              {(!dept.productCategories || dept.productCategories.length > 1) && (
                <ChevronDown className="w-3 h-3" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>

      {/* Right Arrow */}
      {showRightArrow && (
        <button
          onClick={() => scrollDepartments('right')}
          className="absolute right-0 z-10 p-2 bg-gray-800/90 hover:bg-gray-800 text-white rounded-md shadow-lg transition-all outline-none focus:outline-none focus-visible:outline-none"
          aria-label="Scroll departments right"
        >
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </button>
      )}

      {/* Category Dropdown */}
      {hoveredDepartment && hoveredDepartment.productCategories && hoveredDepartment.productCategories.length > 1 && (
        <div
          className="absolute top-full z-50 shadow-lg rounded-b-md border border-gray-600 border-t-0 min-w-[250px] max-w-[400px]"
          style={{
            backgroundColor: themeColors?.left_side_background || '#374151',
            left: `${dropdownPosition}px`,
          }}
        >
          <div className="py-2">
            {hoveredDepartment.productCategories.map((category) => {
              const catActive = selectedCategoryId === category.id && isActiveDepartment(hoveredDepartment.id);
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(hoveredDepartment.id, category.id)}
                  onMouseEnter={e => { if (!catActive) e.currentTarget.style.backgroundColor = `color-mix(in srgb, ${itemColor} 14%, transparent)`; }}
                  onMouseLeave={e => { if (!catActive) e.currentTarget.style.backgroundColor = ''; }}
                  className={`w-full text-left px-4 py-2 text-sm transition-all duration-150 outline-none focus:outline-none focus-visible:outline-none ${!catActive ? 'active:brightness-[0.6]' : ''}`}
                  style={{
                    color: catActive ? activeColor : itemColor,
                    backgroundColor: catActive ? `color-mix(in srgb, ${activeColor} 35%, transparent)` : undefined,
                    fontWeight: catActive ? 600 : 400,
                  }}
                >
                  <div className={catActive ? 'font-semibold' : 'font-medium'}>{category.name}</div>
                  {category.memo && (
                    <div className="text-xs opacity-75 mt-0.5">{category.memo}</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  if (inline) {
    return navContent;
  }

  return (
    <div
      className="hidden md:block border-b border-gray-600"
      style={{ backgroundColor: themeColors?.left_side_background || '#ffffff' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {navContent}
      </div>
    </div>
  );
}
